import { useState, useRef } from 'react';
import { Modal, Stack, Group, Text, Badge, Divider, Button, Tabs, Box, Image, Avatar, UnstyledButton, FileButton, Tooltip } from '@mantine/core';
import { IconEdit, IconPhoto, IconCamera } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useQueryClient } from '@tanstack/react-query';
import { DepreciationInfo } from './DepreciationInfo';
import { AssetQRCode } from './AssetQRCode';
import { AssetChangeHistory } from './AssetChangeHistory';
import { AssetMaintenance } from './AssetMaintenance';
import { AssetDocuments } from './AssetDocuments';
import { AssetLinkedAssets } from './AssetLinkedAssets';
import { AssetNotes } from './AssetNotes';
import { StatusChangeModal } from './StatusChangeModal';
import type { Asset } from '@/types/models';

interface AssetDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  asset: Asset | null;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onRefresh?: () => void;
}

const statusColors: Record<string, string> = {
  Available: 'green',
  Assigned: 'blue',
  Maintenance: 'yellow',
  Retired: 'gray',
  Lost: 'red',
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '35% 1fr',
        gap: '1rem',
        alignItems: 'start',
      }}
    >
      <Box pt={4}>
        <Text size="sm" c="dimmed" fw={500} style={{ textAlign: 'right' }}>
          {label}
        </Text>
      </Box>
      <Box pt={4}>
        <Text size="sm">{value || '-'}</Text>
      </Box>
    </Box>
  );
}

export function AssetDetailsModal({
  opened,
  onClose,
  asset,
  onEdit,
  onDelete,
  onRefresh,
}: AssetDetailsModalProps) {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const resetRef = useRef<() => void>(null);

  if (!asset) return null;

  const handleStatusChangeSuccess = () => {
    // Invalidate assets query to refresh data
    queryClient.invalidateQueries({ queryKey: ['assets'] });
    // Call the parent's refresh callback to update the modal data
    onRefresh?.();
  };

  const handlePhotoUpload = async (file: File | null) => {
    if (!file || !organizationId) return;

    setIsUploadingPhoto(true);
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${asset.id}-${Date.now()}.${fileExt}`;
      const filePath = `${organizationId}/assets/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('asset-photos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('asset-photos')
        .getPublicUrl(filePath);

      // Update asset with new photo URL
      const { error: updateError } = await supabase
        .from('assets')
        .update({ photo_url: publicUrl })
        .eq('id', asset.id);

      if (updateError) throw updateError;

      notifications.show({
        title: 'Photo Updated',
        message: 'Asset photo has been updated successfully',
        color: 'green',
      });

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      onRefresh?.();
    } catch (error) {
      console.error('Photo upload error:', error);
      notifications.show({
        title: 'Upload Failed',
        message: 'Failed to update photo. Please try again.',
        color: 'red',
      });
    } finally {
      setIsUploadingPhoto(false);
      resetRef.current?.();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Asset Details"
      size="xl"
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 120px)',
          overflow: 'hidden',
        },
        content: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100%',
        },
      }}
    >
      {/* Scrollable content area */}
      <Box
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Stack gap="md" p="md">
          {/* Header - responsive layout */}
          <Group justify="apart" align="flex-start" wrap="wrap" gap="md">
            {/* Photo - clickable to update */}
            <FileButton
              resetRef={resetRef}
              onChange={handlePhotoUpload}
              accept="image/png,image/jpeg,image/webp"
              disabled={isUploadingPhoto}
            >
              {(props) => (
                <Tooltip label="Click to update photo">
                  <Box
                    {...props}
                    style={{
                      position: 'relative',
                      cursor: 'pointer',
                      flexShrink: 0,
                      opacity: isUploadingPhoto ? 0.6 : 1,
                    }}
                  >
                    {asset.photo_url ? (
                      <Image
                        src={asset.photo_url}
                        alt={asset.description}
                        w={{ base: 100, sm: 140 }}
                        h={{ base: 100, sm: 140 }}
                        radius="md"
                        fit="cover"
                      />
                    ) : (
                      <Avatar color="gray" size={{ base: 100, sm: 140 }} radius="md">
                        <IconPhoto size={48} />
                      </Avatar>
                    )}
                    {/* Camera overlay */}
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        padding: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconCamera size={16} color="white" />
                    </Box>
                  </Box>
                </Tooltip>
              )}
            </FileButton>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <Text size="xl" fw={700} style={{ wordBreak: 'break-word' }} mb={4}>
                {asset.description}
              </Text>
              <Text size="sm" c="dimmed" mb="sm">
                {asset.brand} {asset.model}
              </Text>
              <Group gap="md" wrap="wrap">
                <UnstyledButton onClick={() => setStatusModalOpen(true)}>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge
                    color={statusColors[asset.status]}
                    size="md"
                    variant="light"
                    style={{ cursor: 'pointer' }}
                  >
                    {asset.status}
                  </Badge>
                </UnstyledButton>
                <div>
                  <Text size="xs" c="dimmed">Tag ID</Text>
                  <Text size="sm" fw={500}>{asset.tag_id}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Serial Number</Text>
                  <Text size="sm" fw={500}>{asset.serial_number || '-'}</Text>
                </div>
                {asset.category?.name && (
                  <div>
                    <Text size="xs" c="dimmed">Category</Text>
                    <Text size="sm" fw={500}>{asset.category.name}</Text>
                  </div>
                )}
              </Group>
            </div>
          </Group>

          <Tabs defaultValue="details" variant="default">
            {/* Clean scrollable tabs - hides scrollbar but allows touch/drag scroll */}
            <Box
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                position: 'sticky',
                top: 0,
                backgroundColor: 'var(--mantine-color-body)',
                zIndex: 5,
                marginLeft: '-1rem',
                marginRight: '-1rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }}
              className="hide-scrollbar"
            >
              <Tabs.List
                style={{
                  flexWrap: 'nowrap',
                  borderBottom: '1px solid var(--mantine-color-gray-2)',
                  gap: 0,
                }}
              >
                <Tabs.Tab value="details" px="md" py="sm">
                  Details
                </Tabs.Tab>
                <Tabs.Tab value="notes" px="md" py="sm">
                  Notes
                </Tabs.Tab>
                <Tabs.Tab value="history" px="md" py="sm">
                  History
                </Tabs.Tab>
                <Tabs.Tab value="maintenance" px="md" py="sm">
                  Maintenance
                </Tabs.Tab>
                <Tabs.Tab value="attachments" px="md" py="sm">
                  Attachments
                </Tabs.Tab>
                <Tabs.Tab value="linked" px="md" py="sm">
                  Linked
                </Tabs.Tab>
                <Tabs.Tab value="depreciation" px="md" py="sm">
                  Depreciation
                </Tabs.Tab>
                <Tabs.Tab value="qrcode" px="md" py="sm">
                  QR
                </Tabs.Tab>
              </Tabs.List>
            </Box>

          <Tabs.Panel value="details" pt="md">
            <Stack gap="md">
              <Divider />

              <Stack gap="sm">
                <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                  Basic Information
                </Text>
                <DetailRow label="Asset Tag" value={asset.tag_id} />
                <DetailRow label="Serial Number" value={asset.serial_number} />
                <DetailRow label="Category" value={asset.category?.name} />
                <DetailRow label="Location" value={asset.location?.name} />
                <DetailRow label="Department" value={asset.department?.name} />
              </Stack>

              <Divider />

              <Stack gap="sm">
                <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                  Assignment
                </Text>
                <DetailRow
                  label="Assigned To"
                  value={
                    asset.assigned_to && asset.assigned_to.length > 0 ? (
                      <Stack gap={4}>
                        {asset.assigned_to.map((person) => (
                          <Text key={person.id} size="sm">
                            {person.full_name}
                            {person.email && (
                              <Text size="xs" c="dimmed" component="span" ml={4}>
                                ({person.email})
                              </Text>
                            )}
                          </Text>
                        ))}
                      </Stack>
                    ) : (
                      'Not assigned'
                    )
                  }
                />
              </Stack>

              <Divider />

              <Stack gap="sm">
                <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                  Purchase Information
                </Text>
                <DetailRow label="Purchase Date" value={formatDate(asset.purchase_date)} />
                <DetailRow label="Purchased From" value={asset.purchased_from} />
                <DetailRow label="Cost" value={formatCurrency(asset.cost)} />
                <DetailRow
                  label="Warranty"
                  value={asset.warranty_period ? `${asset.warranty_period} months` : '-'}
                />
              </Stack>

              {asset.jumpcloud_device_id && (
                <>
                  <Divider />
                  <Stack gap="sm">
                    <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                      JumpCloud Integration
                    </Text>
                    <DetailRow label="Device ID" value={asset.jumpcloud_device_id} />
                    <DetailRow label="Device Name" value={asset.jumpcloud_device_name} />
                  </Stack>
                </>
              )}

              <Divider />

              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Created: {formatDate(asset.created_at)}
                </Text>
                <Text size="xs" c="dimmed">
                  Last Updated: {formatDate(asset.updated_at)}
                </Text>
              </Stack>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="depreciation" pt="md">
            <DepreciationInfo asset={asset} onSuccess={onRefresh} />
          </Tabs.Panel>

          <Tabs.Panel value="notes" pt="md">
            <AssetNotes organizationId={organizationId} assetId={asset.id} />
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <AssetChangeHistory organizationId={organizationId} assetId={asset.id} />
          </Tabs.Panel>

          <Tabs.Panel value="maintenance" pt="md">
            <AssetMaintenance asset={asset} />
          </Tabs.Panel>

          <Tabs.Panel value="attachments" pt="md">
            <AssetDocuments organizationId={organizationId} assetId={asset.id} />
          </Tabs.Panel>

          <Tabs.Panel value="linked" pt="md">
            <AssetLinkedAssets asset={asset} organizationId={organizationId} />
          </Tabs.Panel>

          <Tabs.Panel value="qrcode" pt="md">
            <AssetQRCode asset={asset} />
          </Tabs.Panel>
          </Tabs>
        </Stack>
      </Box>

      {/* Sticky bottom actions */}
      <Box
        p="md"
        style={{
          flexShrink: 0,
          borderTop: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Group justify="flex-end">
          <Button
            leftSection={<IconEdit size={16} />}
            onClick={() => {
              onEdit(asset);
            }}
          >
            Edit
          </Button>
        </Group>
      </Box>

      {/* Photo Preview Modal */}
      <Modal
        opened={photoPreviewOpen}
        onClose={() => setPhotoPreviewOpen(false)}
        title="Asset Photo"
        size="xl"
      >
        {asset.photo_url && (
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <img
              src={asset.photo_url}
              alt={asset.description}
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        )}
      </Modal>

      {/* Status Change Modal */}
      <StatusChangeModal
        opened={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        asset={asset}
        onSuccess={handleStatusChangeSuccess}
      />
    </Modal>
  );
}
